import { z } from "zod";

import { cartItemSchema } from "@/domain/cart/cart-item";
import {
  defaultShippingDestination,
  shippingDestinationSchema,
} from "@/domain/cart/shipping-destination";

export const createOrderIntentItemSchema = z.object({
  productId: z.string().uuid(),
  quantity: z.number().int().min(1).max(999),
});

export const bolivianCustomerPhoneSchema = z
  .string()
  .trim()
  .regex(/^(?:\+?591)?\d{8}$/, "Ingresa un numero de WhatsApp valido de 8 digitos.")
  .transform((value) => {
    const digits = value.startsWith("+") ? value.slice(1) : value;
    return digits.length === 8 ? `591${digits}` : digits;
  });

export function toLocalBolivianPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, "");

  if (digits.startsWith("591") && digits.length > 8) {
    return digits.slice(3, 11);
  }

  return digits.slice(0, 8);
}

export const createOrderIntentSchema = z.object({
  orderIntentId: z.string().uuid(),
  sessionId: z.string().uuid(),
  items: z.array(createOrderIntentItemSchema).min(1).max(100),
  customerPhone: bolivianCustomerPhoneSchema,
  shippingDestination: shippingDestinationSchema.default(defaultShippingDestination),
  note: z.string().max(600).optional(),
  sourceUrl: z.string().url().optional(),
});

export type CreateOrderIntentInput = z.infer<typeof createOrderIntentSchema>;

export const orderIntentSnapshotSchema = createOrderIntentSchema
  .omit({ customerPhone: true })
  .extend({
    items: z.array(cartItemSchema).min(1).max(100),
  });

export type CreateOrderIntentItem = z.infer<typeof createOrderIntentItemSchema>;
export type OrderIntentSnapshotInput = z.infer<typeof orderIntentSnapshotSchema>;

const orderIntentSnapshotItemSchema = cartItemSchema.extend({
  subtotal: z.coerce.number().nonnegative().optional(),
  unitPrice: z.coerce.number().nonnegative().optional(),
});

export const orderIntentReferenceIdSchema = z.string().uuid();

export const orderIntentSnapshotRecordSchema = z.object({
  createdAt: z.string().datetime({ offset: true }),
  currencyCode: z.string().length(3),
  items: z.array(orderIntentSnapshotItemSchema).min(1).max(100),
  note: z.string().max(600).nullable(),
  orderIntentId: orderIntentReferenceIdSchema,
  subtotalEstimated: z.coerce.number().nonnegative(),
  shippingDestination: z.string().max(80).nullable().optional(),
  shippingAmount: z.coerce.number().nonnegative().optional(),
  totalEstimated: z.coerce.number().nonnegative().optional(),
});

export type OrderIntentSnapshotRecord = z.infer<typeof orderIntentSnapshotRecordSchema>;

export interface PublicOrderIntentLine {
  readonly imageUrl: string | null;
  readonly name: string;
  readonly quantity: number;
  readonly subtotal: number;
  readonly unitPrice: number;
}

export interface PublicOrderIntent {
  readonly createdAt: string;
  readonly currencyCode: string;
  readonly items: readonly PublicOrderIntentLine[];
  readonly note: string | null;
  readonly orderIntentId: string;
  readonly shippingAmount: number;
  readonly shippingDestination: string | null;
  readonly subtotalEstimated: number;
  readonly totalEstimated: number;
}

export interface RecordedOrderIntentResult {
  orderIntentId: string;
  referenceUrl: string;
  whatsappUrl: string;
  message: string;
}

export interface OrderIntentResult extends RecordedOrderIntentResult {
  quoteStatus: "created" | "existing" | "failed";
  quoteId: string | null;
  quoteNumber: string | null;
  quoteWarning: string | null;
}
